/*
 * Blog overview — grouped tag filtering, OR within a group, AND across groups.
 *
 * WHY this is browser-side: a single native Shopify blog URL `/blogs/x/tagged/a+b`
 * is AND-only, and Liquid can't read custom query params — so multi-group
 * selections can't be expressed natively. The section therefore renders
 * the FULL current set as cards (each stamped with its group handles in
 * `data-bf-*`), and this element resolves the filter by showing/hiding cards.
 *
 * Split of responsibilities:
 *   - CATEGORY stays 100% native: it lives in the URL path (`/tagged/cat-x`), is
 *     read from `data-bf-active`, drives `blog.articles` server-side, and a
 *     category change re-renders the section (Section Rendering API) to get that
 *     category's full card set. The wired menu keeps working unchanged.
 *   - GROUPS (concern/practice/audience) are client-side: selection lives in the
 *     query string (`?concern=a,b&practice=c`), filtering is instant (no network),
 *     OR within each group, AND across groups (client decision, task 36320 — an
 *     article must carry AT LEAST ONE selected tag from every constrained
 *     group). "Load more" is client-side: `?p=N` = number of pages revealed.
 *   - The FEATURED article (newest of the current view) shows above the grid while
 *     no group filter is active; when a group filter is applied it hides and its
 *     card re-joins the grid so results stay consistent.
 *
 * Shareable + back/forward both work (History API). Ceiling: Shopify's 250-per-
 * page loop, so a single blog/category view filters up to 250 articles.
 */

import { sectionRenderer } from '@theme/section-renderer';

const GROUPS = ['concern', 'practice', 'audience'];

const CHIP_X =
  '<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1.5 1.5 10.5 10.5M10.5 1.5 1.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

/** Desktop shows the sidebar in-flow; ≤1023 shows a full-screen overlay (scroll-locked). */
const OVERLAY_MQ = '(max-width: 1023px)';

function parseHandles(value) {
  return (value || '').trim().split(/\s+/).filter(Boolean);
}
function cap(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

class BlogOverviewFilters extends HTMLElement {
  connectedCallback() {
    this.base = this.dataset.bfBase || '';
    this.perPage = Math.max(1, parseInt(this.dataset.bfPerpage || '9', 10));
    this.sectionId =
      this.getAttribute('section-id') ||
      this.closest('.shopify-section')?.id.replace(/^shopify-section-/, '') ||
      '';

    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onDocPointer = this.onDocPointer.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onPopState = this.onPopState.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    document.addEventListener('click', this.onDocPointer);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('popstate', this.onPopState);

    this.readState();
    this.apply();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocPointer);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('popstate', this.onPopState);
  }

  /** Category from the native path (data-bf-active); groups + page from the query. */
  readState() {
    this.category = parseHandles(this.dataset.bfActive).filter((h) => h.startsWith('cat-'));
    const params = new URLSearchParams(window.location.search);
    this.selection = {};
    for (const g of GROUPS) {
      const v = params.get(g);
      this.selection[g] = v ? v.split(',').filter(Boolean).map((x) => `${g}-${x}`) : [];
    }
    const p = parseInt(params.get('p') || '1', 10);
    this.page = Number.isFinite(p) && p > 0 ? p : 1;
  }

  get groupActive() {
    return GROUPS.some((g) => (this.selection[g] || []).length > 0);
  }

  buildUrl({ category = this.category, selection = this.selection, page = this.page } = {}) {
    const path = category.length ? `${this.base}/tagged/${category.join('+')}` : this.base;
    const params = new URLSearchParams();
    for (const g of GROUPS) {
      const sel = selection[g] || [];
      if (sel.length) params.set(g, sel.map((h) => h.slice(g.length + 1)).join(','));
    }
    if (page > 1) params.set('p', String(page));
    const q = params.toString();
    return q ? `${path}?${q}` : path;
  }

  /** OR within each constrained group (any selected tag matches), AND across groups. */
  cardMatches(card) {
    for (const g of GROUPS) {
      const sel = this.selection[g];
      if (!sel.length) continue;
      const has = (card.dataset['bf' + cap(g)] || '').split(' ').filter(Boolean);
      if (!sel.some((h) => has.includes(h))) return false;
    }
    return true;
  }

  apply() {
    const groupActive = this.groupActive;
    const cards = [...this.querySelectorAll('[data-bf-card]')];
    const featured = this.querySelector('[data-bf-featured]');
    const featuredCard = this.querySelector('[data-bf-featured-card]');

    // Featured block shows only in the unfiltered view; its card then leaves the grid.
    if (featured) featured.hidden = groupActive;

    const matchedAll = cards.filter((c) => this.cardMatches(c));
    const pool = featuredCard && !groupActive ? matchedAll.filter((c) => c !== featuredCard) : matchedAll;

    // "Load more" model: `page` = how many client-side pages are revealed, so
    // everything from the start up to page × perPage is visible (Jan review item 5).
    const totalPages = Math.max(1, Math.ceil(pool.length / this.perPage));
    if (this.page > totalPages) this.page = totalPages;
    const end = this.page * this.perPage;

    // Inline style beats the card's `display:flex`, so no [hidden] CSS needed here.
    cards.forEach((c) => {
      c.style.display = 'none';
    });
    pool.slice(0, end).forEach((c) => {
      c.style.display = '';
    });

    this.renderCount(matchedAll.length);
    this.toggleEmpty(matchedAll.length === 0);
    this.renderTotal();
    this.renderChips();
    this.renderViewResults(matchedAll.length);
    this.syncCheckboxes();
    this.syncCategory();
    this.renderLoadMore(pool.length, end);
  }

  /** Applied-filter chips (Figma sidebar + mobile overlay). One chip per active
   *  group tag, with a remove "×"; the block hides when nothing is selected. */
  renderChips() {
    const wrap = this.querySelector('[data-bf-applied]');
    const chips = this.querySelector('[data-bf-chips]');
    if (!wrap || !chips) return;
    const frag = document.createDocumentFragment();
    let count = 0;
    for (const g of GROUPS) {
      for (const handle of this.selection[g] || []) {
        const input = this.querySelector(`[data-bf-group="${g}"] [data-tag-handle="${handle}"]`);
        const label = input?.closest('.bf-opt')?.querySelector('.bf-opt__label')?.textContent?.trim() || handle;
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'bf-chip';
        chip.dataset.bfChipRemove = '';
        chip.dataset.bfChipGroup = g;
        chip.dataset.tagHandle = handle;
        chip.setAttribute('aria-label', `Remove ${label}`);
        const text = document.createElement('span');
        text.textContent = label;
        const x = document.createElement('span');
        x.className = 'bf-chip__x';
        x.innerHTML = CHIP_X;
        chip.append(text, x);
        frag.append(chip);
        count += 1;
      }
    }
    chips.replaceChildren(frag);
    wrap.hidden = count === 0;
  }

  /** "View results (N)" on the mobile overlay reflects the current match count. */
  renderViewResults(n) {
    const el = this.querySelector('[data-bf-viewcount]');
    if (el) el.textContent = String(n);
  }

  /** Localized "N Blog Posts" — templates arrive via data attrs (locale files). */
  renderCount(n) {
    const el = this.querySelector('[data-bf-count]');
    if (!el) return;
    const one = this.dataset.bfCountOne || '1 Blog Post';
    const other = this.dataset.bfCountOther || '[COUNT] Blog Posts';
    el.textContent = n === 1 ? one : other.replace('[COUNT]', String(n));
  }

  toggleEmpty(isEmpty) {
    const grid = this.querySelector('.blog-overview__grid');
    const empty = this.querySelector('[data-bf-filter-empty]');
    if (grid) grid.style.display = isEmpty ? 'none' : '';
    if (empty) empty.hidden = !isEmpty;
  }

  /** Total active group filters → the "(N)" badge on the Show Filters toggle. */
  renderTotal() {
    const total = GROUPS.reduce((n, g) => n + (this.selection[g] || []).length, 0);
    const badge = this.querySelector('[data-bf-total]');
    if (badge) {
      badge.textContent = `(${total})`;
      badge.hidden = total === 0;
    }
    const toggle = this.querySelector('[data-bf-filters-toggle]');
    if (toggle) toggle.classList.toggle('bf-filters__toggle--active', total > 0);
  }

  syncCheckboxes() {
    for (const input of this.querySelectorAll('[data-bf-tag]')) {
      const g = input.closest('[data-bf-group]')?.dataset.bfGroup;
      input.checked = g ? this.selection[g].includes(input.dataset.tagHandle) : false;
    }
  }

  /** Reflect the active category onto the tabs + carry the group selection into their hrefs. */
  syncCategory() {
    const hasCategory = this.category.length > 0;
    for (const tab of this.querySelectorAll('.bf-cat')) {
      const h = tab.dataset.tagHandle;
      const isActive = h ? this.category.includes(h) : !hasCategory;
      tab.classList.toggle('is-active', isActive);
      if (isActive) tab.setAttribute('aria-current', 'page');
      else tab.removeAttribute('aria-current');
      tab.setAttribute('href', this.buildUrl({ category: h ? [h] : [], page: 1 }));
    }
  }

  /* ---- Events ------------------------------------------------------------- */

  onChange(event) {
    const input = event.target.closest('[data-bf-tag]');
    if (!input) return;
    const g = input.closest('[data-bf-group]')?.dataset.bfGroup;
    if (!g) return;
    const handle = input.dataset.tagHandle;
    if (input.checked) {
      if (!this.selection[g].includes(handle)) this.selection[g].push(handle);
    } else {
      this.selection[g] = this.selection[g].filter((h) => h !== handle);
    }
    this.page = 1;
    history.pushState({ bf: true }, '', this.buildUrl());
    this.apply(); // instant, no network
  }

  onClick(event) {
    // "Load more" — reveal the next client-side page in place. replaceState, not
    // pushState: Back should leave the page, not un-reveal rows; `?p=N` still
    // deep-links to the same revealed depth.
    if (event.target.closest('[data-bf-load-more]')) {
      event.preventDefault();
      this.page += 1;
      history.replaceState({ bf: true }, '', this.buildUrl());
      this.apply();
      return;
    }

    // Remove a single applied-filter chip.
    const chip = event.target.closest('[data-bf-chip-remove]');
    if (chip) {
      event.preventDefault();
      const g = chip.dataset.bfChipGroup;
      const handle = chip.dataset.tagHandle;
      if (g && this.selection[g]) {
        this.selection[g] = this.selection[g].filter((h) => h !== handle);
        this.page = 1;
        history.pushState({ bf: true }, '', this.buildUrl());
        this.apply();
      }
      return;
    }

    // Show/hide the filter panel.
    const filtersToggle = event.target.closest('[data-bf-filters-toggle]');
    if (filtersToggle) {
      event.preventDefault();
      this.toggleFilters();
      return;
    }

    // Close the mobile overlay (× button) or confirm with "View results".
    if (event.target.closest('[data-bf-filters-close]') || event.target.closest('[data-bf-viewresults]')) {
      event.preventDefault();
      this.toggleFilters(false);
      return;
    }

    // "All categories" → drop the category (re-render its full set), keep groups.
    if (event.target.closest('[data-bf-all]')) {
      event.preventDefault();
      this.navigateCategory(this.buildUrl({ category: [], page: 1 }));
      return;
    }

    // Category tab → swap category (native re-render), keep the group selection.
    const cat = event.target.closest('[data-bf-cat]');
    if (cat) {
      event.preventDefault();
      const h = cat.dataset.tagHandle;
      const next = this.category.includes(h) ? [] : [h];
      this.navigateCategory(this.buildUrl({ category: next, page: 1 }));
      return;
    }

    // Clear all → wipe groups (client) and the category (native, if any).
    if (event.target.closest('[data-bf-clear]')) {
      event.preventDefault();
      for (const g of GROUPS) this.selection[g] = [];
      this.page = 1;
      if (this.category.length) {
        this.navigateCategory(this.buildUrl({ category: [], page: 1 }));
      } else {
        history.pushState({ bf: true }, '', this.buildUrl());
        this.apply();
      }
    }
  }

  /** Category change needs the server to re-render that category's full card set. */
  async navigateCategory(url, { push = true } = {}) {
    if (!this.sectionId) {
      window.location.assign(url);
      return;
    }
    this.setBusy(true);
    try {
      if (push) history.pushState({ bf: true }, '', url);
      await sectionRenderer.renderSection(this.sectionId, { url: new URL(url, window.location.origin) });
      this.readState(); // fresh category from the morphed data-bf-active + groups from the URL
      this.apply();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      window.location.assign(url);
    } finally {
      this.setBusy(false);
    }
  }

  onPopState() {
    const match = window.location.pathname.match(/\/tagged\/([^/?]+)/);
    const urlCategory = match ? match[1].split('+').filter((h) => h.startsWith('cat-')) : [];
    if (urlCategory.join('+') === this.category.join('+')) {
      // Same category → only groups/page changed; re-filter client-side.
      this.readState();
      this.apply();
    } else {
      // Category changed → re-render that category's set (history already moved).
      this.navigateCategory(window.location.href, { push: false });
    }
  }

  setBusy(isBusy) {
    this.classList.toggle('bf-loading', isBusy);
    this.toggleAttribute('aria-busy', isBusy);
  }

  /* ---- Filter panel ------------------------------------------------------- */

  /** Open/close the filter panel. `.bf-sidebar-open` on the host drives both the
   *  desktop sidebar reveal + content reflow and the mobile overlay; on mobile it
   *  also scroll-locks the body so the page behind the overlay can't scroll. */
  toggleFilters(force) {
    const toggle = this.querySelector('[data-bf-filters-toggle]');
    const open = force !== undefined ? force : !this.classList.contains('bf-sidebar-open');
    this.classList.toggle('bf-sidebar-open', open);
    if (toggle) toggle.setAttribute('aria-expanded', String(open));
    const overlay = window.matchMedia(OVERLAY_MQ).matches;
    document.body.classList.toggle('bf-body-lock', open && overlay);
  }

  onDocPointer(event) {
    // Only the desktop in-flow sidebar closes on an outside click; on the mobile
    // overlay the ×/"View results" controls handle closing. Clicks inside the
    // sidebar, on the toggle, or on a category tab (which re-renders) never close.
    if (!this.classList.contains('bf-sidebar-open')) return;
    if (window.matchMedia(OVERLAY_MQ).matches) return;
    if (
      event.target.closest('[data-bf-sidebar]') ||
      event.target.closest('[data-bf-filters-toggle]') ||
      event.target.closest('.bf-cat')
    ) {
      return;
    }
    this.toggleFilters(false);
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.toggleFilters(false);
  }

  /* ---- Load more (client-side) --------------------------------------------- */

  /** Show the "Load more" button only while more matches exist than are revealed. */
  renderLoadMore(total, shown) {
    const wrap = this.querySelector('[data-bf-loadmore]');
    if (wrap) wrap.hidden = shown >= total;
  }
}

if (!customElements.get('blog-overview-filters')) {
  customElements.define('blog-overview-filters', BlogOverviewFilters);
}
